/**
 * VersionBar - Horizontal version chip bar
 *
 * Shows all versions of current content as clickable chips.
 * Color-coded by type: archive (gray), override (amber),
 * transform (blue), analysis (green), checkpoint (muted).
 *
 * B7: Added checkpoint type, tooltips with word count delta,
 * and right-click context menu for restore.
 *
 * @module @humanizer/studio/components/workspace/VersionBar
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export type VersionType = 'archive' | 'override' | 'transform' | 'analysis' | 'checkpoint';

export interface ContentVersion {
  id: string;
  type: VersionType;
  label: string;
  timestamp: number;
  model?: string;
  wordCount?: number;
}

export interface VersionBarProps {
  versions: ContentVersion[];
  activeVersionId: string | null;
  onVersionSelect: (versionId: string) => void;
  onVersionRestore?: (versionId: string) => void;
  className?: string;
}

function relativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function VersionBar({
  versions,
  activeVersionId,
  onVersionSelect,
  onVersionRestore,
  className = '',
}: VersionBarProps): React.ReactElement | null {
  if (versions.length <= 1) return null;

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; versionId: string } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClick = useCallback(
    (versionId: string) => () => {
      onVersionSelect(versionId);
    },
    [onVersionSelect]
  );

  const handleContextMenu = useCallback((e: React.MouseEvent, versionId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, versionId });
  }, []);

  const handleRestore = useCallback(() => {
    if (contextMenu && onVersionRestore) {
      onVersionRestore(contextMenu.versionId);
    }
    setContextMenu(null);
  }, [contextMenu, onVersionRestore]);

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [contextMenu]);

  return (
    <div className={`version-bar ${className}`} role="tablist" aria-label="Content versions">
      {versions.map((version, i) => {
        const isActive = version.id === activeVersionId;
        const prevVersion = i > 0 ? versions[i - 1] : null;
        const wordDelta = version.wordCount != null && prevVersion?.wordCount != null
          ? version.wordCount - prevVersion.wordCount
          : null;
        const deltaStr = wordDelta != null
          ? wordDelta > 0 ? ` (+${wordDelta}w)` : wordDelta < 0 ? ` (${wordDelta}w)` : ''
          : '';

        const tooltip = `${version.label}${version.model ? ` (${version.model})` : ''} \u2014 ${relativeTime(version.timestamp)}${version.wordCount != null ? `, ${version.wordCount}w` : ''}${deltaStr}`;

        return (
          <button
            key={version.id}
            className={`version-bar__chip version-bar__chip--${version.type} ${isActive ? 'version-bar__chip--active' : ''}`}
            onClick={handleClick(version.id)}
            onContextMenu={(e) => handleContextMenu(e, version.id)}
            role="tab"
            aria-selected={isActive}
            title={tooltip}
          >
            <span className="version-bar__chip-label">{version.label}</span>
          </button>
        );
      })}

      {contextMenu && onVersionRestore && (
        <div
          ref={menuRef}
          className="version-bar__context-menu"
          style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="version-bar__context-item"
            onClick={handleRestore}
          >
            Restore to this version
          </button>
        </div>
      )}
    </div>
  );
}

export default VersionBar;
